<ng-container *transloco="let t">
  <div class="main-content">
    <div class="topbar"></div>
    <div class="profile-main-card">
      <div class="profile-sidebar">
        @for (section of dataSections; track section.id) {
          <div
            class="section-button"
            [class.active]="section.id == activeSection"
            pRipple
            (click)="activeSection = section.id"
          >
            <span class="material-symbols-outlined section-icon">
              {{ section.icon }}
            </span>
            <span class="section-label">{{ t(section.title) }}</span>
          </div>
        }
      </div>
      <div class="profile-section">
        @for (card of currentSectionCards; track $index) {
          <div class="profile-card">
            <h2 class="card-title" [innerHTML]="t(card.title)"></h2>
            @if (card.subtitle) {
              <p class="card-subtitle" [innerHTML]="t(card.subtitle)"></p>
            }

            <ng-container
              [ngTemplateOutlet]="card.template"
              [ngTemplateOutletContext]="{ card: card }"
            >
            </ng-container>
            @if (card.showFormButtons ?? true) {
              <div class="buttons-row">
                @if (card.isEditing) {
                  <p-button
                    [label]="t('cancel')"
                    class="cancel-button"
                    icon="pi pi-undo"
                    severity="secondary"
                    (click)="cardCancelEdits(card)"
                  />

                  <p-button
                    [label]="t('save')"
                    class="save-button"
                    icon="pi pi-save"
                    [disabled]="
                      card.form?.invalid ||
                      (card.validatorFn && !card.validatorFn(card.data))
                    "
                    (click)="cardSaveEdits(card)"
                    [loading]="card.isLoading"
                  />
                } @else {
                  <p-button
                    [label]="t('edit')"
                    class="edit-button"
                    icon="pi pi-pencil"
                    severity="secondary"
                    (click)="cardEnableEdit(card)"
                    [loading]="card.isLoading"
                  />
                }
              </div>
            }
          </div>
        }
      </div>
    </div>
  </div>

  <ng-template #baseProfileInfo let-card="card">
    {{ loadForm(card, form) }}
    <form #form="ngForm" class="fields-container">
      <p-floatlabel variant="in" class="username-input">
        <input
          pInputText
          [(ngModel)]="card.data.username"
          id="username-profile"
          autocomplete="off"
          name="username"
          required
          [disabled]="!card.isEditing || card.isLoading"
        />
        <label for="username-profile">{{ t("username") }}</label>
      </p-floatlabel>
      <p-floatlabel variant="in" class="first-name-input">
        <input
          pInputText
          [(ngModel)]="card.data.firstName"
          id="first-name-profile"
          autocomplete="off"
          name="first-name"
          [disabled]="!card.isEditing || card.isLoading"
        />
        <label for="first-name-profile">{{ t("firstName") }}</label>
      </p-floatlabel>
      <p-floatlabel variant="in" class="last-name-input">
        <input
          pInputText
          [(ngModel)]="card.data.lastName"
          id="last-name-profile"
          autocomplete="off"
          name="last-name"
          [disabled]="!card.isEditing || card.isLoading"
        />
        <label for="last-name-profile">{{ t("lastName") }}</label>
      </p-floatlabel>
      <p-floatlabel variant="in">
        <p-select
          inputId="gender-profile"
          name="gender"
          [(ngModel)]="card.data.gender"
          autocomplete="off"
          [options]="[
            { label: t('male'), value: 'M' },
            { label: t('female'), value: 'F' },
            { label: t('otherOrUnspecified'), value: 'O' },
          ]"
          appendTo="body"
          styleClass="w-full"
          required
          [disabled]="!card.isEditing || card.isLoading"
        />
        <label for="gender-profile">
          {{ t("gender") }}
        </label>
      </p-floatlabel>
    </form>
  </ng-template>

  <ng-template #profileEmail let-card="card">
    {{ loadForm(card, form) }}
    <form #form="ngForm" class="fields-container">
      <p-floatlabel variant="in" class="email-input">
        <input
          pInputText
          [(ngModel)]="card.data.email"
          id="email-profile"
          autocomplete="off"
          name="email"
          required
          [disabled]="!card.isEditing || card.isLoading"
        />
        <label for="email-profile">{{ t("email") }}</label>
      </p-floatlabel>
    </form>
  </ng-template>

  <ng-template #profilePassword let-card="card">
    {{ loadForm(card, form) }}
    <form #form="ngForm" class="fields-container" autocomplete="off">
      <p-floatlabel variant="in" class="password-input">
        <p-password
          #self
          [(ngModel)]="card.data.password"
          inputId="password-profile"
          name="password"
          required
          [toggleMask]="true"
          [disabled]="!card.isEditing || card.isLoading"
          [feedback]="true"
          [mediumRegex]="mediumRegex"
          [strongRegex]="strongRegex"
          appendTo="body"
          autocomplete="new-password"
        />
        <label for="password-profile">{{ t("newPassword") }}</label>
      </p-floatlabel>
      <p-floatlabel variant="in" class="password-input">
        <p-password
          [(ngModel)]="card.data.passwordConfirm"
          inputId="password-confirm-profile"
          name="passwordConfirm"
          appendTo="body"
          [feedback]="false"
          required
          [toggleMask]="true"
          [disabled]="!card.isEditing || card.isLoading"
          autocomplete="new-password"
        />
        <label for="password-confirm-profile">{{ t("confirmPassword") }}</label>
      </p-floatlabel>
    </form>
  </ng-template>

  <ng-template #profileConsents let-card="card">
    <div class="consents-container">
      @for (consent of card.originalData; track consent.consentType) {
        <div class="consent-row">
          <div class="consent-info">
            <p
              class="consent-description"
              [innerHTML]="t(consent.consentType + 'ConsentLabel')"
            ></p>
            <p class="consent-details">
              {{
                t("consentVersionPlaceholder", {
                  version: consent.consentVersion,
                })
              }}
              |
              {{
                consent.consentAccepted == null
                  ? t("toReview")
                  : t("consentLastUpdatedPlaceholder", {
                      status: consent.consentAccepted
                        ? t("accepted")
                        : t("denied"),
                      date:
                        consent.consentDate
                        | translocoDate
                          : { dateStyle: "short", timeStyle: "medium" },
                    })
              }}
            </p>
          </div>
          <p-checkbox
            [(ngModel)]="card.data[$index].consentAccepted"
            [disabled]="!card.isEditing || card.isLoading"
            [binary]="true"
            [indeterminate]="card.data[$index].consentAccepted == null"
          />
        </div>
      }
    </div>
  </ng-template>

  <ng-template #profileDataDownload let-card="card">
    <div class="buttons-row">
      <p-button
        [label]="t('requestData')"
        class="request-data-button"
        icon="pi pi-download"
        severity="primary"
        (click)="cardSaveEdits(card)"
        [loading]="card.isLoading"
      />
    </div>
  </ng-template>

  <ng-template #profileDelete let-card="card">
    <div class="buttons-row">
      <p-button
        [label]="t('requestDeletion')"
        class="request-deletion-button"
        icon="pi pi-trash"
        severity="danger"
        (click)="cardSaveEdits(card)"
        [loading]="card.isLoading"
      />
    </div>
  </ng-template>
</ng-container>
